import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import SEO from "@/components/SEO";
import { MapPin, Navigation, ArrowRight, Globe2, Users } from "lucide-react";
import { COUNTRY_LIST } from "@/data/locations";
import { getAllCities } from "@/data/cities";

const LocationsIndex = () => {
  const africa = COUNTRY_LIST.filter((c) => c.type === "africa");
  const diaspora = COUNTRY_LIST.filter((c) => c.type === "diaspora");

  return (
    <div>
      <SEO
        title="Shop African by location — Countries, Cities & Local Areas"
        description="Browse African products, food and services by location. From Lagos to London, Accra to Atlanta, find verified African sellers and businesses near you on AfriMarket."
        path="/locations"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "AfriMarket Locations",
          "url": "https://afrimarket.org/locations",
          "description": "Browse African products, food and services by country, city or local area.",
        }}
      />

      <section className="container-narrow py-16">
        <div className="max-w-3xl">
          <div className="label-overline text-[#C05A3C] mb-3">Shop by location</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            African commerce, worldwide. Pick a place to start.
          </h1>
          <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
            AfriMarket is organised by country, city and local area — so you can shop products direct from Africa, or book trusted services right around the corner. From Lagos couture to London braiders, every seller is verified.
          </p>

          {/* Near me CTA */}
          <Link to="/near-me" data-testid="near-me-cta">
            <Card className="mt-8 p-6 bg-gradient-to-br from-[#C05A3C] to-[#1A2024] text-white border-0 cursor-pointer hover:scale-[1.01] transition-transform">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center shrink-0">
                  <Navigation className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <div className="font-display text-xl">Find African businesses near you</div>
                  <div className="text-sm text-white/80 mt-1">Use your location to discover braiders, caterers, restaurants and sellers in your neighbourhood</div>
                </div>
                <ArrowRight className="w-5 h-5" />
              </div>
            </Card>
          </Link>
        </div>
      </section>

      <section className="container-narrow pb-14">
        <div className="label-overline text-[#C05A3C] mb-3 flex items-center gap-2">
          <Globe2 className="w-3.5 h-3.5" /> Shop from Africa
        </div>
        <h2 className="font-display text-3xl mb-6">Direct from {africa.length} African countries</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="africa-grid">
          {africa.map((c) => {
            const cityCount = getAllCities(c.slug).length;
            return (
            <Link
              key={c.slug}
              to={`/${c.slug}`}
              data-testid={`country-${c.slug}`}
              className="group bg-white border border-black/10 rounded-xl p-5 hover:border-[#C05A3C] hover:shadow-sm transition-all"
            >
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl leading-none">{c.flag}</span>
                <span className="font-display text-xl group-hover:text-[#C05A3C] transition-colors">{c.name}</span>
              </div>
              <div className="text-xs text-muted-foreground">
                {cityCount} {cityCount === 1 ? "city" : "cities"} mapped
              </div>
            </Link>
            );
          })}
        </div>
      </section>

      <section className="container-narrow pb-16">
        <div className="label-overline text-[#C05A3C] mb-3 flex items-center gap-2">
          <Users className="w-3.5 h-3.5" /> Local services in your country
        </div>
        <h2 className="font-display text-3xl mb-6">Where the African diaspora lives</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="diaspora-grid">
          {diaspora.map((c) => {
            const cityCount = getAllCities(c.slug).length;
            return (
            <Link
              key={c.slug}
              to={`/${c.slug}`}
              data-testid={`country-${c.slug}`}
              className="group bg-white border border-black/10 rounded-xl p-5 hover:border-[#C05A3C] hover:shadow-sm transition-all"
            >
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl leading-none">{c.flag}</span>
                <span className="font-display text-xl group-hover:text-[#C05A3C] transition-colors">{c.name}</span>
              </div>
              <div className="text-xs text-muted-foreground">{cityCount} cities mapped</div>
            </Link>
            );
          })}
        </div>
      </section>

      <section className="container-narrow pb-20">
        <Card className="bg-[#1A2024] text-white rounded-2xl border-0 p-8 lg:p-10 text-center">
          <h2 className="font-display text-3xl">Your city not listed?</h2>
          <p className="mt-3 text-white/80 max-w-xl mx-auto">
            We&apos;re adding new African and diaspora cities every week. Become a seller to bring AfriMarket to your area, or message us with a request.
          </p>
          <div className="mt-6">
            <Link to="/register" data-testid="locations-cta-seller">
              <Button className="bg-[#C05A3C] hover:bg-[#D46B4E] rounded-md h-12 px-8 font-semibold">
                Become a Seller
              </Button>
            </Link>
          </div>
        </Card>
      </section>
    </div>
  );
};

export default LocationsIndex;
